import { Image } from 'react-datocms';
import { Alert } from '@zopauk/web-ui';
import { Post } from '@/components/Post';
import { Pagination } from '@/components/Pagination';
import { SimpleHero } from '@/components/SimpleHero';
import { Placeholder } from '@/components/Placeholder';
import type { CategoryContext } from '@/utils/models/category';
import {
  type AuthorForPostListFragment,
  type PostsForCategoryFragment,
  useCategoryQuery,
} from '@/utils/dato/generated';

const PostsForCategory = ({ pageContext }: { pageContext: CategoryContext } & PostsForCategoryFragment) => {
  const { error, data } = useCategoryQuery({
    perPage: pageContext.pagination.perPage,
    skip: pageContext.pagination.skip,
    slug: pageContext.slug,
  });

  if (data?.category) {
    return (
      <div className="relative mt-10 mb-8 lg:mb-11 flex flex-col max-w-screen-xl mx-auto w-full px-4">
        <SimpleHero
          image={
            data.category.featuredImage?.responsiveImage ? (
              <Image
                data={data.category.featuredImage?.responsiveImage}
                style={{ margin: '0 auto' }}
                objectPosition="50% 50%"
                priority={true}
              />
            ) : undefined
          }
          title={data.category?.title || 'Uncategorised'}
          description={data.category.description || ''}
        />

        <div className="px-5 mx-auto max-w-[825px]">
          <p className="text-lg mb-6">
            Page {pageContext.pagination.currentPage} of {pageContext.pagination.totalPages}
          </p>

          {!data.category?.posts?.length ? (
            <Alert variant="info">{data?.category?.title} doesn't have any articles to display.</Alert>
          ) : null}

          {data.category?.posts.map((post) => (
            <Post
              image={
                post?.featuredImage?.responsiveImage ? (
                  <Image
                    data={post.featuredImage.responsiveImage}
                    style={{ margin: '0 auto' }}
                    objectPosition="50% 50%"
                  />
                ) : undefined
              }
              author={post.author as AuthorForPostListFragment}
              categories={post.categories}
              excerpt={post.excerpt}
              title={post.title}
              date={post.date}
              slug={post.slug}
              key={post.id}
            />
          ))}
        </div>

        <Pagination basePath={`/blog/category/${pageContext.slug}`} {...pageContext.pagination} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="relative mt-10 mb-8 lg:mb-11 flex flex-col max-w-screen-xl mx-auto w-full px-4">
        <Alert variant="warning">We've been unable to load this Categories posts</Alert>
      </div>
    );
  }

  return (
    <div className="mt-10 mb-8 lg:mb-11 mt-10 mb-8 lg:mb-11 max-w-screen-xl mx-auto px-4">
      <div className="bg-base-100 rounded-lg px-4 pt-8 pb-10 mb-10 text-center">
        <div className="flex flex-col items-center">
          <Placeholder className="mx-auto w-[380px] h-[251px]" />
          <Placeholder className="mx-auto mb-0 w-[200px] h-[26px]" />
        </div>
      </div>

      <div className="px-5 mx-auto max-w-[825px]">
        <Placeholder className="h-[182px]" />
        <Placeholder className="h-[182px]" />
        <Placeholder className="h-[182px]" />
        <Placeholder className="h-[182px]" />
        <Placeholder className="mx-auto w-[180px] h-[52px]" />
      </div>
    </div>
  );
};

export default PostsForCategory;
